import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Group, List, Stack, Text } from "@mantine/core";
import { BellRing, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { hasPushSubscription, isPushSupported, subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { browserPushStatus, needsHomeScreen, readPwaEnv, type PushStatus } from "@/lib/pwa";

export const PUSH_STATUS_KEY = ["browser-push-status"] as const;

async function readPushStatus(): Promise<PushStatus> {
  const supported = isPushSupported();
  return browserPushStatus({
    needsHomeScreen: needsHomeScreen(readPwaEnv()),
    supported,
    permission: supported ? Notification.permission : null,
    subscribed: supported ? await hasPushSubscription() : false,
  });
}

interface ViewProps {
  status: PushStatus;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
  /** Without the card's title (the guide step already says it). */
  bare?: boolean;
}

/** Whether this browser shows notifications, and the one thing to do about it — presentational. */
export function BrowserNotificationsView({ status, busy, onEnable, onDisable, bare = false }: ViewProps) {
  let body: React.ReactNode;
  switch (status) {
    case "needs-home-screen":
      body = (
        <Alert color="blue" variant="light" icon={<Smartphone size={18} />} title="Add to Home Screen first">
          <Stack gap={4}>
            <Text fz="sm">On iPhone and iPad, notifications only work in the portal app on your Home Screen.</Text>
            <List type="ordered" size="sm">
              <List.Item>In Safari, tap Share, then Add to Home Screen.</List.Item>
              <List.Item>Open the portal from your Home Screen and sign in.</List.Item>
              <List.Item>Switch notifications on there.</List.Item>
            </List>
          </Stack>
        </Alert>
      );
      break;
    case "unsupported":
      body = <Text fz="sm" c="dimmed">This browser cannot show notifications. Keep the portal open in a tab to be rung there, or use another browser.</Text>;
      break;
    case "blocked":
      body = <Text fz="sm" c="dimmed">Notifications are blocked for this site. Allow them in your browser settings for this site, then reload the page.</Text>;
      break;
    case "on":
      body = (
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text fz="sm">This browser shows notifications.</Text>
          <Button variant="default" size="xs" loading={busy} onClick={onDisable}>
            Turn off
          </Button>
        </Group>
      );
      break;
    default:
      body = (
        <Stack gap="xs">
          <Text fz="sm" c="dimmed">Get told about visitor calls and other updates, even when the portal is in the background.</Text>
          <Button leftSection={<BellRing size={16} />} loading={busy} onClick={onEnable} w="fit-content">
            Turn on notifications
          </Button>
        </Stack>
      );
  }
  if (bare) return <Stack data-testid="browser-notifications">{body}</Stack>;
  return (
    <Card withBorder padding="lg" data-testid="browser-notifications">
      <Stack gap="sm">
        <Text fw={600}>Notifications on this device</Text>
        {body}
      </Stack>
    </Card>
  );
}

/** Notification settings and the "Answer visitor calls" guide. `onEnabled` fires after a subscription is saved. */
export function BrowserNotificationsCard({ onEnabled, bare = false }: { onEnabled?: () => void; bare?: boolean } = {}) {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: PUSH_STATUS_KEY, queryFn: readPushStatus, staleTime: 0 });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: PUSH_STATUS_KEY });
    qc.invalidateQueries({ queryKey: ["comms-preferences"] });
  };
  const enable = useMutation({
    mutationFn: subscribeToPush,
    onSuccess: () => {
      refresh();
      toast.success("Notifications are on for this browser");
      onEnabled?.();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't turn on notifications"),
  });
  const disable = useMutation({
    mutationFn: unsubscribeFromPush,
    onSuccess: refresh,
    onError: () => toast.error("Couldn't turn off notifications"),
  });
  if (!status) return null;
  return (
    <BrowserNotificationsView
      status={status}
      busy={enable.isPending || disable.isPending}
      onEnable={() => enable.mutate()}
      onDisable={() => disable.mutate()}
      bare={bare}
    />
  );
}
